import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useToast } from "@chakra-ui/react";
import { useApiRequest } from "../../../hooks/useApiRequest";
import { hasRole } from "../../../lib/auth";
import { settings } from "../../../shared/settings";
import type { FormType } from "./types";

const base = () => settings.environment.VITE_PROCESS_HUB_BASE_URL ?? "";

interface ProcessHubFormsContextValue {
  forms: FormType[];
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
  /** Atualização otimista de um formulário na lista em memória (ex.: toggle ativo). */
  patchForm: (id: number, patch: Partial<FormType>) => void;
}

const ProcessHubFormsContext = createContext<ProcessHubFormsContextValue | null>(null);

/**
 * Fonte única da lista de formulários do Process Hub.
 *
 * Antes disto, a tabela de admin (useProcessHubForms) e a grade "preencher uma
 * solicitação" (LeaderFormsGrid) cada uma fazia o seu próprio GET /forms e
 * mantinha o seu próprio estado — então um formulário criado pelo RH só
 * aparecia na grade após F5. Com o contexto, as duas view consomem a mesma
 * lista e um refresh() (disparado após criar/editar/tornar inativo) atualiza
 * ambas ao vivo.
 */
export function ProcessHubFormsProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const { request } = useApiRequest();
  const isRhAdmin = hasRole("rh") || hasRole("admin");

  const [forms, setForms] = useState<FormType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const data = await request({
        url: `${base()}/api/v1/forms`,
        method: "GET",
        params: isRhAdmin ? { include_inactive: "true" } : {},
        skipToast: true,
      });
      setForms(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
      toast({ title: "Erro ao carregar formulários", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [request, toast, isRhAdmin]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const patchForm = useCallback((id: number, patch: Partial<FormType>) => {
    setForms((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }, []);

  return (
    <ProcessHubFormsContext.Provider value={{ forms, loading, error, refresh, patchForm }}>
      {children}
    </ProcessHubFormsContext.Provider>
  );
}

export function useProcessHubFormsData() {
  const ctx = useContext(ProcessHubFormsContext);
  if (!ctx) {
    throw new Error("useProcessHubFormsData deve ser usado dentro de ProcessHubFormsProvider");
  }
  return ctx;
}